import { createContext, useContext, useMemo } from "react";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";
import { red } from "@mui/material/colors";
import { useTheme as useAppTheme } from "./ThemeContext";

/* ------------------------------------------------------------------
   Bridges the app theme into MUI.

   Tailwind's `dark:` variants only style elements we author. MUI renders
   Dialog / Menu / Select dropdowns / Tooltip / DatePicker popovers from its
   own palette, so without this they stay light — a white dialog on a dark
   page. Driving MUI's `palette.mode` from the same source keeps them in step.

   Surfaces read the CSS tokens in index.css (var(--app-…)), so MUI paper,
   dialogs and menus follow all four themes — classic/glass × light/dark —
   exactly like .app-card and .app-glass do. palette.background still holds
   real colors because MUI runs color math (alpha, contrast) on it.

   NESTED PROVIDERS: a `<ThemeProvider theme={createTheme(...)}>` inside the
   app replaces this theme for its subtree, losing every override below.
   Pages that need a provider (material-table wrappers did) should pass
   `useAppMuiTheme()` instead of building their own.
------------------------------------------------------------------ */

const FONT_STACK = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"SF Pro Text"',
  '"SF Pro Display"',
  "Inter",
  '"Segoe UI Variable Text"',
  '"Segoe UI"',
  "Roboto",
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(",");

const GLASS_FILTER = "var(--app-glass-filter)";

// Floating layers (menus, popovers, dialogs) — same material as .app-glass.
const floatingSurface = (isGlass) => ({
  backgroundColor: "var(--app-popover-bg)",
  backgroundImage: isGlass ? "var(--app-glass-sheen)" : "none",
  border: "1px solid var(--app-popover-border)",
  boxShadow: "var(--app-popover-shadow)",
  ...(isGlass && {
    WebkitBackdropFilter: GLASS_FILTER,
    backdropFilter: GLASS_FILTER,
  }),
});

export function buildAppTheme({ isDark, isGlass }) {
  const primary = isDark
    ? { main: "#38bdf8", light: "#7dd3fc", dark: "#0284c7", contrastText: "#082f49" }
    : { main: "#0284c7", light: "#38bdf8", dark: "#0369a1", contrastText: "#ffffff" };

  return createTheme({
    palette: {
      mode: isDark ? "dark" : "light",
      primary,
      secondary: { main: isDark ? "#2dd4bf" : "#0f766e" },
      error: { main: isDark ? "#f87171" : red.A700 },
      // Match the slate surfaces used by the Tailwind palette and the
      // .app-* helpers in index.css so MUI panels sit flush with ours.
      background: isDark
        ? { default: "#0f172a", paper: "#1e293b" }
        : { default: "#ffffff", paper: "#ffffff" },
      text: isDark
        ? { primary: "#f1f5f9", secondary: "#94a3b8" }
        : { primary: "#0f172a", secondary: "#64748b" },
      divider: isDark ? "rgba(148, 163, 184, 0.2)" : "rgba(15, 23, 42, 0.1)",
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: FONT_STACK,
      button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
      h1: { fontWeight: 700, letterSpacing: "-0.02em" },
      h2: { fontWeight: 700, letterSpacing: "-0.02em" },
      h3: { fontWeight: 700, letterSpacing: "-0.015em" },
      h4: { fontWeight: 700, letterSpacing: "-0.01em" },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: { body: { fontFamily: FONT_STACK } },
      },
      MuiPaper: {
        styleOverrides: {
          // Kills MUI's dark-mode elevation overlay gradient; depth comes
          // from the shadow tokens instead.
          root: { backgroundImage: "none" },
          rounded: { borderRadius: 16 },
          outlined: { borderColor: "var(--app-card-border)" },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: "var(--app-card-bg)",
            border: "1px solid var(--app-card-border)",
            boxShadow: "var(--app-card-shadow)",
            borderRadius: 16,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: "var(--app-card-bg)",
            border: "1px solid var(--app-card-border)",
            boxShadow: "none",
            "&:before": { display: "none" },
            "&:first-of-type": { borderTopLeftRadius: 14, borderTopRightRadius: 14 },
            "&:last-of-type": { borderBottomLeftRadius: 14, borderBottomRightRadius: 14 },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { ...floatingSurface(isGlass), borderRadius: 22 },
          // Full-screen dialogs (image editor, phone layouts) fill the
          // viewport — no floating-card rounding, border or drop shadow.
          paperFullScreen: { borderRadius: 0, border: 0, boxShadow: "none" },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontWeight: 700, letterSpacing: "-0.01em" } },
      },
      MuiBackdrop: {
        styleOverrides: {
          // Menus and popovers use an invisible backdrop; only real modal
          // scrims get the tint + blur.
          root: ({ ownerState }) =>
            ownerState.invisible
              ? {}
              : {
                  backgroundColor: "var(--app-scrim)",
                  ...(isGlass && {
                    WebkitBackdropFilter: "blur(6px)",
                    backdropFilter: "blur(6px)",
                  }),
                },
        },
      },
      MuiPopover: {
        styleOverrides: { paper: { ...floatingSurface(isGlass), borderRadius: 14 } },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { ...floatingSurface(isGlass), borderRadius: 14 },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            minHeight: 36,
            "&:hover": { backgroundColor: "var(--app-hover)" },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: { ...floatingSurface(isGlass), borderRadius: 14 },
          listbox: { padding: 6 },
          option: { borderRadius: 8 },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "var(--app-drawer-bg)",
            borderColor: "var(--app-chrome-border)",
            ...(isGlass && {
              WebkitBackdropFilter: GLASS_FILTER,
              backdropFilter: GLASS_FILTER,
            }),
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark
              ? "rgba(241, 245, 249, 0.92)"
              : "rgba(15, 23, 42, 0.88)",
            color: isDark ? "#0f172a" : "#f8fafc",
            fontSize: 12,
            fontWeight: 500,
            borderRadius: 8,
            padding: "6px 10px",
            WebkitBackdropFilter: "blur(12px)",
            backdropFilter: "blur(12px)",
          },
          arrow: {
            color: isDark ? "rgba(241, 245, 249, 0.92)" : "rgba(15, 23, 42, 0.88)",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          // Capsule buttons, like iOS/macOS.
          root: { borderRadius: 9999, paddingLeft: 16, paddingRight: 16 },
          sizeSmall: { paddingLeft: 12, paddingRight: 12 },
          sizeLarge: { paddingLeft: 22, paddingRight: 22 },
          contained: {
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.22), 0 1px 2px rgba(2,6,23,0.18)",
            "&:hover": {
              boxShadow:
                "inset 0 1px 0 rgba(255,255,255,0.22), 0 4px 12px -2px rgba(2,6,23,0.25)",
            },
          },
          outlined: {
            backgroundColor: isGlass ? "var(--app-card-bg)" : undefined,
          },
          // Dark mode's primary is the lighter sky-400 so text buttons, tabs,
          // labels and links stay legible on slate. Filled buttons keep the
          // sky-600 + white used by every Tailwind button on the site.
          ...(isDark && {
            containedPrimary: {
              backgroundColor: "#0284c7",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#0369a1" },
            },
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { "&:hover": { backgroundColor: "var(--app-hover)" } },
        },
      },
      MuiButtonGroup: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 9999 } },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          // Segmented control look.
          root: {
            backgroundColor: "var(--app-hover)",
            borderRadius: 10,
            padding: 3,
            gap: 2,
          },
          grouped: {
            border: "0 !important",
            borderRadius: "8px !important",
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            padding: "5px 12px",
            color: "var(--app-text-muted)",
            "&.Mui-selected": {
              backgroundColor: isDark ? "rgba(255,255,255,0.14)" : "#ffffff",
              color: "var(--app-text)",
              boxShadow: isDark ? "none" : "0 1px 3px rgba(15,23,42,0.12)",
            },
            "&.Mui-selected:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.18)" : "#ffffff",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: "var(--app-input-bg)",
            transition: "box-shadow 0.15s ease",
            "&.Mui-focused": { boxShadow: "0 0 0 3px var(--app-ring)" },
          },
          notchedOutline: { borderColor: "var(--app-input-border)" },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            backgroundColor: "var(--app-hover)",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 9999, fontWeight: 500 },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 3, borderRadius: 3 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            minHeight: 44,
            letterSpacing: 0,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: { root: { borderRadius: 14 } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: "var(--app-divider)" },
          head: {
            fontWeight: 600,
            color: "var(--app-text-muted)",
            fontSize: "0.75rem",
            letterSpacing: "0.02em",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&.MuiTableRow-hover:hover": { backgroundColor: "var(--app-hover)" },
          },
        },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 14 } },
      },
      MuiSnackbarContent: {
        styleOverrides: { root: { borderRadius: 14 } },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 9999 }, bar: { borderRadius: 9999 } },
      },
      MuiSwitch: {
        // iOS-style switch.
        styleOverrides: {
          root: { width: 46, height: 28, padding: 0, margin: 8, overflow: "visible" },
          switchBase: {
            padding: 2,
            "&.Mui-checked": {
              transform: "translateX(18px)",
              color: "#fff",
              "& + .MuiSwitch-track": {
                backgroundColor: isDark ? "#30d158" : "#34c759",
                opacity: 1,
                border: 0,
              },
            },
            "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.4 },
          },
          thumb: {
            width: 24,
            height: 24,
            boxShadow: "0 2px 4px rgba(0,0,0,0.2), 0 0 0 0.5px rgba(0,0,0,0.04)",
          },
          track: {
            borderRadius: 14,
            backgroundColor: isDark ? "#39393d" : "#e2e5ea",
            opacity: 1,
            transition: "background-color 0.2s ease",
          },
        },
      },
    },
  });
}

const AppMuiThemeContext = createContext(null);

/** The app's MUI theme — pass this to any nested MUI ThemeProvider. */
export function useAppMuiTheme() {
  return useContext(AppMuiThemeContext);
}

export default function MuiThemeBridge({ children }) {
  const { isDark, isGlass } = useAppTheme();

  const theme = useMemo(
    () => buildAppTheme({ isDark, isGlass }),
    [isDark, isGlass]
  );

  return (
    <AppMuiThemeContext.Provider value={theme}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </AppMuiThemeContext.Provider>
  );
}
